import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetSectionData } from '@themes/common/data';
import { GetProductPalletValues, IsMobile, RoundOff, } from '@themes/common/utils';
import clsx from 'clsx';
import ExtraProduct from './extra-products';
import debounce from 'lodash/debounce';
import { FormatMoney } from '@components/Shared/Utils/price';
import ExtraProductDisplay from './extra-products-display';
import useProductPage from '../useProductPage';
import { StringReplaceCurlyBraces } from '@commons/replaceCurly';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import CartService from '@themes/common/Cart/CartService';
// Import extracted components and utilities
import FormInput from './FormInput';
import { PalletDisplay, PriceDisplay } from './DisplayComponents';
import { useProductInputState } from '../hooks/useProductInputState';
import { useVariantManagement } from '../hooks/useVariantManagement';
import { handleInputChange, handleButtonAction } from '../utils';
import { isProductSample, filterHorn } from '../constants';
const ProductSectionData = GetSectionData('static-product');
const ProductInput = React.memo(({ id: variantId, settings, product: productData, quantity = 1, minimumQuantity = 1, withPallets = true, inputChange, inputChangeDebounce, onBlur, children, loading, isCart, extraProducts: extraProductsData, variantInput, type: formInputType, }) => {
    var _a, _b, _c, _d;
    const priceFormat = useMemo(() => (isCart ? settings.money_format : settings.money_format.replace('amount_no_decimals', 'amount')), [isCart, settings.money_format]);
    const previousVariantId = useRef(null);
    const [variantChangeLoading, setVariantChangeLoading] = useState(false);
    const [{ variantId: id, product, extraProducts, variants }, setProductPage] = useProductPage(variantId, quantity, extraProductsData, productData, isCart);
    const initialLoad = useRef(true);
    const [selectedPoolCorners, setSelectedPoolCorners] = useState((_a = (extraProducts !== null && extraProducts !== void 0 ? extraProducts : []).filter(filterHorn).map((m) => m.variant_id)) !== null && _a !== void 0 ? _a : []);
    const [selectedPoolCorner, setSelectedPoolCorner] = useState(((_b = (extraProducts !== null && extraProducts !== void 0 ? extraProducts : []).filter(filterHorn).map((m) => m.variant_id)) !== null && _b !== void 0 ? _b : []).length > 0);
    // Use the extracted hook for input state management
    const { state, setState, isPool, isBags, isStair, bagSize, bagDensity, extra_product_pool_corner, selectedPoolCorner: hookSelectedPoolCorner, setSelectedPoolCorner: hookSetSelectedPoolCorner, updateValues, updatePoolCornerSelection, } = useProductInputState(product, extraProducts, quantity, minimumQuantity, id);
    const { qty, rawQuantity, fields, areaFieds, inputs, pallets, breakingPallets, tippings } = state;
    // Use the extracted hook for variant management
    const { variantChangeLoading: hookVariantChangeLoading } = useVariantManagement(variantId, isCart || false, productData, setProductPage, variants);
    const price = useMemo(() => product.price, [product.price]);
    const initialProductTotal = useMemo(() => price * quantity, [price, quantity]);
    const [totalDisplay, setTotalDisplay] = useState(FormatMoney(initialProductTotal, priceFormat));
    const is_sample = isProductSample(product);
    const extra_product_pool_corners = useMemo(() => (extra_product_pool_corner ? [extra_product_pool_corner] : []), [extra_product_pool_corner]);
    const extra_products = useMemo(() => extraProducts.filter((f) => f.variant_id !== (extra_product_pool_corner === null || extra_product_pool_corner === void 0 ? void 0 : extra_product_pool_corner.variant_id)), [extra_product_pool_corner, extraProducts]);
    const type = useMemo(() => {
        if (formInputType) {
            return formInputType;
        }
        if (isPool) {
            return 'pool';
        }
        if (isBags) {
            return 'bag';
        }
        if (isStair) {
            return 'stair';
        }
        return 'default';
    }, [formInputType, isPool, isBags, isStair]);
    // Initialize product page set
    useEffect(() => {
        if (!isCart) {
            setProductPage((state) => {
                state.variantId = variantId;
                state.product = productData;
                state.extraProducts = extraProductsData;
                state.quantity = quantity;
            });
        }
    }, [variantId, productData, extraProductsData, quantity, setProductPage, isCart]);
    const onSelectedPoolCorner = useCallback((ids) => {
        if (extra_product_pool_corner) {
            const pool_selected = ids.length > 0 ? ids.includes(extra_product_pool_corner.variant_id) : false;
            setSelectedPoolCorner(pool_selected);
            updatePoolCornerSelection(pool_selected);
            setSelectedPoolCorners(ids);
        }
    }, [extra_product_pool_corner, updatePoolCornerSelection]);
    const handleInputChangeCallback = useCallback((key, currentVal) => {
        handleInputChange(key, currentVal, updateValues);
    }, [updateValues]);
    const handleButtonActionCallback = useCallback((key, action) => {
        handleButtonAction(key, action, inputs, updateValues);
    }, [inputs, updateValues]);
    const { pallet, pallet_qty, total_weight_qty, breaking_pallet, breaking_pallet_qty, tipping, tipping_qty } = useMemo(() => GetProductPalletValues(id, qty), [id, qty]);
    useEffect(() => {
        setState((currentState) => (Object.assign(Object.assign({}, currentState), { pallets: pallet_qty || (total_weight_qty !== null && total_weight_qty !== void 0 ? total_weight_qty : 0), breakingPallets: breaking_pallet_qty, tippings: tipping_qty })));
    }, [pallet_qty, breaking_pallet_qty, total_weight_qty, tipping_qty, setState]);
    useEffect(() => {
        const productTotal = price * rawQuantity;
        const formatedMoney = FormatMoney(productTotal, priceFormat);
        setTotalDisplay(formatedMoney);
        const qtyInput = jQuery('#product-quantity-input');
        qtyInput.val(qty);
        qtyInput.trigger('change');
        if (!isCart) {
            const form = qtyInput.closest('form');
            const button = form.find('button[data-product-atc]');
            const valid = CartService.CheckMinimumQuantity(product, qty);
            if (!valid.isValid) {
                button.attr('disabled', 'disabled');
                button.addClass('disabled');
            }
            else {
                button.removeAttr('disabled');
                button.removeClass('disabled');
            }
            setProductPage((state) => {
                state.quantity = qty;
            });
        }
    }, [rawQuantity, qty, product, settings, price, pallets, breakingPallets, withPallets, setProductPage, isCart, minimumQuantity, priceFormat]);
    useEffect(() => {
        if (inputChange && !initialLoad.current) {
            inputChange(qty);
        }
        const handleInputChangeDebounce = () => {
            if (inputChangeDebounce && !initialLoad.current) {
                inputChangeDebounce(qty);
            }
        };
        const callBackChangeHandler = debounce(handleInputChangeDebounce, IsMobile() ? 2500 : 1500);
        if (!initialLoad.current) {
            callBackChangeHandler();
        }
        initialLoad.current = false;
        return () => {
            callBackChangeHandler.cancel();
        };
    }, [qty, inputChange, inputChangeDebounce]);
    useEffect(() => {
        if (isCart) {
            return;
        }
        const element = jQuery(`[data-section-type="static-product"][data-section-data]`);
        if (!element.length) {
            return;
        }
        const section = jQuery(`#shopify-section-${element.data('section-id')}`);
        const priceContainer = section.find('[data-price-container]');
        const priceElement = section.find('[data-price-container] [data-price]');
        const compareAtPriceContainer = section.find('[data-price-compare-container]');
        const compareAtPriceElement = section.find('[data-price-compare-container] [data-price-compare]');
        const salesBadgeContainer = section.find('[data-badge-sales]');
        const priceDefaultUnit = section.find('[data-default-unit-price]');
        const onSale = Number(product.unit_price) < Number(product.unit_compare_at_price);
        priceElement.html(`${FormatMoney(product.unit_price)} <span class="sb-units"> / ${product.unit}</span>`);
        compareAtPriceElement.html(`${FormatMoney(product.unit_compare_at_price)} <span class="sb-units"> / ${product.unit}</span>`);
        compareAtPriceContainer.toggleClass('visible', onSale);
        priceContainer.toggleClass('price__current--on-sale', onSale);
        if (product.price !== product.unit_price) {
            priceDefaultUnit.html(`<span class="money">${FormatMoney(product.price, priceFormat)}</span> <span class="sb-units"> / ${product.default_unit}</span>`);
        }
        else {
            priceDefaultUnit.html('');
        }
        if (settings.product.sales_badge && onSale) {
            salesBadgeContainer.css('display', 'block');
            let saleBadge = settings.translations.product_price.badge.sale;
            if (settings.product.sales_badge_style === 'percent') {
                saleBadge = StringReplaceCurlyBraces(settings.translations.product_price.badge.sale_percentage_single_html, {
                    savings: Math.round(((product.unit_compare_at_price - product.unit_price) / product.unit_compare_at_price) * 100),
                });
            }
            else if (settings.product.sales_badge_style === 'money') {
                saleBadge = StringReplaceCurlyBraces(settings.translations.product_price.badge.sale_money_single_html, {
                    savings: `${FormatMoney(product.unit_compare_at_price - product.unit_price)} <span class="sb-units"> / ${product.unit}</span>`,
                });
            }
            salesBadgeContainer.html(saleBadge);
        }
        else {
            salesBadgeContainer.css('display', 'none');
        }
    }, [isCart, product, settings]);
    if (!Object.keys(inputs).length) {
        return _jsx(_Fragment, {});
    }
    return (_jsxs(_Fragment, { children: [variantChangeLoading && (_jsxs(_Fragment, { children: [_jsx(LinearProgress, { color: 'primary', sx: {
                            position: 'absolute',
                            width: '100%',
                            top: 0,
                            left: 0,
                            zIndex: 50,
                        } }), _jsx(LinearProgress, { color: 'primary', sx: {
                            position: 'absolute',
                            width: '100%',
                            bottom: 0,
                            left: 0,
                            zIndex: 50,
                        } }), _jsx(CircularProgress, { sx: {
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 9999,
                        } })] })), _jsx("input", { type: 'hidden', name: 'product[properties][_type]', value: type, "aria-labelledby": 'Form type' }), _jsxs("div", Object.assign({ className: clsx('product-input-container-wrapper', {
                    'wrapper-pool': isPool,
                    'wrapper-bag': isBags,
                    'wrapper-stair': isStair,
                }) }, { children: [!is_sample && fields.length > 0 && isStair && (_jsx("div", Object.assign({ className: clsx('product-input-container stair') }, { children: fields
                            .filter((f) => f.key === 'Length' || f.key === 'Height')
                            .map(({ key, unitValue, hideButtons }) => {
                            const inputId = `product[properties][${unitValue}]`;
                            const input_cart_value = RoundOff(inputs[key]);
                            return (_jsx(FormInput, { id: inputId, field: key, unit: unitValue, value: input_cart_value === null || input_cart_value === void 0 ? void 0 : input_cart_value.toString(), loading: loading, isCart: isCart, onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur, hideButtons: hideButtons }, key));
                        }) }))), !is_sample && fields.length > 0 && (_jsx("div", Object.assign({ className: clsx('product-input-container', {
                            pool: isPool,
                        }) }, { children: fields
                            .filter((f) => (f.unit !== 'PCE' || !isPool || isCart) && (!isStair || (isStair && !(f.key === 'Length' || f.key === 'Height'))))
                            .map(({ key, unitValue, factorValue, hideButtons, minimumQuantity }) => {
                            const inputId = `product[properties][${unitValue}]`;
                            const input_cart_value = RoundOff(inputs[key]);
                            return (_jsx(FormInput, { id: inputId, field: key, unit: unitValue, value: input_cart_value === null || input_cart_value === void 0 ? void 0 : input_cart_value.toString(), loading: loading, isCart: isCart, price: isPool ? undefined : price, factorValue: isPool ? undefined : factorValue, onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur, hideButtons: hideButtons, minimumQuantity: minimumQuantity }, key));
                        }) }))), isPool && !is_sample && fields.length > 0 && !isCart && (_jsx("div", Object.assign({ className: 'product-input-container pool' }, { children: fields
                            .filter((f) => f.unit === 'PCE')
                            .map(({ key, unitValue, factorValue, hideButtons, minimumQuantity }) => {
                            const inputId = `product[properties][${unitValue}]`;
                            const input_cart_value = RoundOff(inputs[key]);
                            return (_jsx(FormInput, { id: inputId, field: key, unit: unitValue, value: input_cart_value === null || input_cart_value === void 0 ? void 0 : input_cart_value.toString(), loading: loading, isCart: isCart, price: price, factorValue: factorValue, onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur, hideButtons: hideButtons, minimumQuantity: minimumQuantity }, key));
                        }) }))), !is_sample && areaFieds.length > 0 && (_jsxs(_Fragment, { children: [!isCart && (_jsx("div", Object.assign({ className: 'product-input-container' }, { children: areaFieds
                                    .filter((f) => f.key !== 'PCE' && f.key !== 'M3')
                                    .map(({ key, unitValue, factorValue, hideButtons, minimumQuantity }) => {
                                    const inputId = `product[properties][${unitValue}]`;
                                    const input_cart_value = RoundOff(inputs[key]);
                                    return (_jsx(FormInput, { id: inputId, field: key, unit: unitValue, value: input_cart_value === null || input_cart_value === void 0 ? void 0 : input_cart_value.toString(), loading: loading, isCart: isCart, price: isBags ? undefined : price, factorValue: isBags ? undefined : factorValue, onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur, hideButtons: hideButtons, minimumQuantity: minimumQuantity }, key));
                                }) }))), _jsxs("div", Object.assign({ className: 'product-input-container' }, { children: [_jsx(FormInput, { id: 'input-M3', field: 'M3', unit: 'M3', value: (_c = RoundOff(inputs['M3'])) === null || _c === void 0 ? void 0 : _c.toString(), loading: loading, isCart: isCart, className: 'not-equal', price: price, factorValue: 1 / (bagSize / bagDensity), onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur }), !isCart && _jsx("div", Object.assign({ className: 'product-input-wrapper not-equal no-item' }, { children: "\u00A0" })), _jsx(FormInput, { id: 'input-PCE', field: 'PCE', unit: 'PCE', value: (_d = RoundOff(inputs['PCE'])) === null || _d === void 0 ? void 0 : _d.toString(), loading: loading, isCart: isCart, price: price, factorValue: 1, className: 'not-equal', onChange: handleInputChangeCallback, onButtonClick: handleButtonActionCallback, onBlur: onBlur, minimumQuantity: minimumQuantity })] }))] })), is_sample && (_jsxs("div", Object.assign({ className: 'product-input-container' }, { children: [settings.translations.product.quantity, ": ", quantity] })))] })), _jsx(PriceDisplay, { totalDisplay: totalDisplay, isCart: isCart || false, settings: settings }), _jsx(PalletDisplay, { pallets: pallets, breakingPallets: breakingPallets, tippings: tippings, pallet: pallet, breaking_pallet: breaking_pallet, tipping: tipping, settings: settings }), !!extra_product_pool_corner && !isCart && (_jsxs("div", Object.assign({ className: 'extra-products--wrapper' }, { children: [_jsx("h5", Object.assign({ className: 'extra-products--header' }, { children: settings.translations.product.pool_calculator })), _jsx(ExtraProduct, { extra_products: extra_product_pool_corners, qty: qty, settings: settings, center_display: true, replace_title: settings.translations.product.pool_corner_title, display_price: selectedPoolCorner, onSelected: onSelectedPoolCorner, selected_items: selectedPoolCorners })] }))), !isCart && _jsx(ExtraProductDisplay, { extra_products: extra_products, qty: qty, settings: settings }), children] }));
});
ProductInput.displayName = 'ProductInput';
export default ProductInput;
